import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clientsAPI } from '../lib/api';
import { QualityBadge, scoreClient } from '../lib/dataQuality';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Search, Plus, MoreHorizontal, Eye, Trash2, Mail, Users, Filter, Download, X, Loader2, UserPlus, RotateCcw,
  ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { ScreenshotImport } from '../components/ScreenshotImport';

const LEAD_SOURCES = ['walk_in', 'cold_call', 'referral', 'online', 'other'];
const EMPLOYMENT_TYPES = ['employed', 'self_employed', 'contractor', 'retired', 'unemployed'];

const CurrencyInput = ({ value, onChange, ...props }) => {
  const [display, setDisplay] = React.useState(value || '');
  const [focused, setFocused] = React.useState(false);

  React.useEffect(() => {
    if (!focused) {
      setDisplay(value || '');
    }
  }, [value, focused]);

  const formatForDisplay = (v) => {
    const num = parseFloat(v);
    if (isNaN(num) || !v) return v;
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(num);
  };

  return (
    <Input
      {...props}
      type={focused ? 'number' : 'text'}
      value={focused ? display : (display ? formatForDisplay(display) : '')}
      onChange={(e) => { setDisplay(e.target.value); onChange(e); }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

const Clients = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ lead_source: '', postcode: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newClient, setNewClient] = useState({
    first_name: '', last_name: '', company_name: '', email: '', phone: '', dob: '',
    current_address: '', postcode: '',
    income: '', employment_type: '',
    credit_issues: false, credit_issues_notes: '',
    lead_source: '', referral_partner_name: '', fact_find_complete: false,
    vulnerable_customer: false, advice_type: '',
    additional_applicants: [],
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);

  // Bulk delete state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    if (location.state?.openAddDialog) {
      setShowAddDialog(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => { loadClients(); }, [filters]); // eslint-disable-line

  const loadClients = async () => {
    try {
      setLoading(true);
      const params = { ...filters };
      if (search) params.search = search;
      const data = await clientsAPI.getAll(params);
      setClients(data.clients || []);
      setSelectedIds(new Set()); // clear selection on reload
    } catch (err) {
      console.error('Failed to load clients:', err);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => loadClients();

  const handleAddClient = async () => {
    try {
      const clientData = {
        ...newClient,
        income: newClient.income ? parseFloat(newClient.income) : null,
      };
      await clientsAPI.create(clientData);
      toast.success('Client created successfully');
      setShowAddDialog(false);
      setNewClient({ first_name: '', last_name: '', company_name: '', email: '', phone: '', dob: '', current_address: '', postcode: '', income: '', employment_type: '', credit_issues: false, credit_issues_notes: '', lead_source: '', referral_partner_name: '', fact_find_complete: false, vulnerable_customer: false, advice_type: '', additional_applicants: [] });
      loadClients();
    } catch (err) {
      toast.error(err.message || 'Failed to create client');
    }
  };

  const handleDeleteClient = async () => {
    if (!clientToDelete) return;
    try {
      await clientsAPI.delete(clientToDelete.client_id);
      toast.success('Client deleted');
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      loadClients();
    } catch (err) {
      toast.error(err.message || 'Failed to delete client');
    }
  };

  // Bulk selection helpers
  const allSelected = clients.length > 0 && clients.every(c => selectedIds.has(c.client_id));
  const someSelected = selectedIds.size > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(clients.map(c => c.client_id)));
    }
  };

  const toggleSelectOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    let successCount = 0;
    let failCount = 0;
    for (const id of selectedIds) {
      try {
        await clientsAPI.delete(id);
        successCount++;
      } catch {
        failCount++;
      }
    }
    setBulkDeleting(false);
    setBulkDeleteDialogOpen(false);
    if (successCount > 0) toast.success(`${successCount} client${successCount > 1 ? 's' : ''} deleted`);
    if (failCount > 0) toast.error(`${failCount} client${failCount > 1 ? 's' : ''} could not be deleted`);
    loadClients();
  };

  const handleExportClients = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/clients`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      let filename = 'KK_Mortgage_Clients_Export.xlsx';
      const cd = response.headers.get('content-disposition');
      if (cd) { const match = cd.match(/filename=(.+)/); if (match) filename = match[1]; }
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Client data exported successfully!');
    } catch (err) {
      toast.error('Failed to export client data');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const parts = d.split('T')[0].split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return d;
  };
  const formatStatus = (s) => s?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '-';

  const getRowBg = () => 'hover:bg-slate-50 dark:hover:bg-slate-800 dark:text-slate-100';

  const clearFilters = () => setFilters({ lead_source: '', postcode: '' });

  // ── Column sort ───────────────────────────────────────────────────────────
  const [sort, setSort] = useState({ key: null, dir: 'asc' });

  const clientSortValue = (c, key) => {
    switch (key) {
      case 'name':         return `${c.first_name || ''} ${c.last_name || ''}`.trim().toLowerCase();
      case 'email':        return (c.email || '').toLowerCase();
      case 'phone':        return (c.phone || '').toLowerCase();
      case 'postcode':     return (c.postcode || '').toLowerCase();
      case 'income':       return c.income || 0;
      case 'employment':   return (c.employment_type || '').toLowerCase();
      case 'lead_source':  return (c.lead_source || '').toLowerCase();
      case 'quality':      return scoreClient(c)?.percent ?? 0;
      case 'created':      return c.created_at || '';
      default:             return '';
    }
  };

  const sortedClients = (() => {
    if (!sort.key) return clients;
    const sorted = [...clients].sort((a, b) => {
      const va = clientSortValue(a, sort.key);
      const vb = clientSortValue(b, sort.key);
      if (typeof va === 'number' && typeof vb === 'number') return va - vb;
      return String(va).localeCompare(String(vb));
    });
    return sort.dir === 'desc' ? sorted.reverse() : sorted;
  })();

  const handleSortClick = (key) => {
    if (sort.key === key) setSort({ key, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
    else setSort({ key, dir: 'asc' });
  };

  const SortHead = ({ label, sortKey, className = '' }) => {
    const active = sort.key === sortKey;
    const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <TableHead className={className}>
        <button
          type="button"
          onClick={() => handleSortClick(sortKey)}
          className={`inline-flex items-center gap-1 hover:text-slate-900 dark:hover:text-white transition-colors ${active ? 'text-slate-900 dark:text-white font-semibold' : ''}`}
        >
          {label}
          <Icon className={`h-3.5 w-3.5 ${active ? 'opacity-100' : 'opacity-40'}`} />
        </button>
      </TableHead>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-red-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>Clients</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Manage your client database</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportClients} disabled={exporting} data-testid="export-clients-btn">
            {exporting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Exporting...</> : <><Download className="h-4 w-4 mr-2" />Export to Excel</>}
          </Button>
          <Button className="bg-red-600 hover:bg-red-700" onClick={() => setShowAddDialog(true)} data-testid="add-client-btn">
            <Plus className="h-4 w-4 mr-2" />Add Client
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 flex gap-2">
              <Input placeholder="Search clients by name, email, phone..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} className="flex-1" data-testid="client-search-input" />
              <Button onClick={handleSearch}><Search className="h-4 w-4 mr-2" />Search</Button>
              <Button variant="outline" onClick={() => setShowFilters(!showFilters)}><Filter className="h-4 w-4 mr-2" />Filters</Button>
            </div>
          </div>
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Lead Source</Label>
                <Select value={filters.lead_source || 'all'} onValueChange={(v) => setFilters({ ...filters, lead_source: v === 'all' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="All sources" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    {LEAD_SOURCES.map((s) => <SelectItem key={s} value={s}>{formatStatus(s)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Postcode</Label>
                <Input placeholder="e.g. SW1A" value={filters.postcode} onChange={(e) => setFilters({ ...filters, postcode: e.target.value })} />
              </div>
              <div className="flex items-end">
                <Button variant="ghost" onClick={clearFilters} className="text-slate-500"><X className="h-4 w-4 mr-1" />Clear</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Delete Bar */}
      <div className="flex items-center justify-end">
        {someSelected && (
          <div className="flex items-center gap-3 px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <span className="text-sm font-medium text-red-700 dark:text-red-400">
              {selectedIds.size} client{selectedIds.size > 1 ? 's' : ''} selected
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="text-slate-500 hover:text-slate-700 h-7 px-2"
              onClick={() => setSelectedIds(new Set())}
            >
              <X className="h-3.5 w-3.5 mr-1" />Deselect
            </Button>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 h-7"
              onClick={() => setBulkDeleteDialogOpen(true)}
              data-testid="bulk-delete-clients-btn"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />Delete Selected
            </Button>
          </div>
        )}
      </div>

      {/* Client Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {clients.length === 0 ? (
            <div className="text-center py-12" data-testid="no-clients-message">
              <Users className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">No clients found</h3>
              <p className="text-slate-500 dark:text-slate-400 mb-4">Get started by adding your first client.</p>
              <Button className="bg-red-600 hover:bg-red-700" onClick={() => setShowAddDialog(true)}><Plus className="h-4 w-4 mr-2" />Add Client</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[44px] pl-4">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all clients"
                        data-testid="select-all-clients"
                      />
                    </TableHead>
                    <SortHead label="Client Name" sortKey="name" />
                    <SortHead label="Email"       sortKey="email" />
                    <SortHead label="Phone"       sortKey="phone" />
                    <SortHead label="Postcode"    sortKey="postcode" />
                    <SortHead label="Income"      sortKey="income" />
                    <SortHead label="Employment"  sortKey="employment" />
                    <SortHead label="Lead Source" sortKey="lead_source" />
                    <SortHead label="Quality"     sortKey="quality" />
                    <SortHead label="Created"     sortKey="created" />
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedClients.map((c) => (
                    <TableRow
                      key={c.client_id}
                      className={`cursor-pointer transition-colors ${selectedIds.has(c.client_id) ? 'bg-red-50/60 dark:bg-red-900/10' : getRowBg(c)}`}
                      onClick={() => navigate(`/clients/${c.client_id}`)}
                      data-testid={`client-row-${c.client_id}`}
                    >
                      <TableCell className="pl-4" onClick={(e) => { e.stopPropagation(); toggleSelectOne(c.client_id); }}>
                        <Checkbox
                          checked={selectedIds.has(c.client_id)}
                          onCheckedChange={() => toggleSelectOne(c.client_id)}
                          aria-label={`Select ${c.first_name} ${c.last_name}`}
                          data-testid={`select-client-${c.client_id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{c.first_name} {c.last_name}</p>
                        {Array.isArray(c.additional_applicants) && c.additional_applicants.length > 0 && (
                          <div className="mt-0.5 space-y-0.5">
                            {c.additional_applicants
                              .filter(a => (a?.first_name || a?.last_name))
                              .map((a, i) => (
                                <p key={i} className="text-xs text-slate-500 dark:text-slate-400 leading-tight">
                                  + {`${a.first_name || ''} ${a.last_name || ''}`.trim()}
                                </p>
                              ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {c.email ? <span className="flex items-center gap-1"><Mail className="h-3 w-3 text-slate-400" />{c.email}</span> : '-'}
                      </TableCell>
                      <TableCell className="text-sm">{c.phone || '-'}</TableCell>
                      <TableCell>{c.postcode || '-'}</TableCell>
                      <TableCell className="font-medium">
                        {c.income ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(c.income) : '-'}
                      </TableCell>
                      <TableCell>
                        {c.employment_type ? <Badge variant="outline" className="text-xs">{formatStatus(c.employment_type)}</Badge> : '-'}
                      </TableCell>
                      <TableCell>
                        {c.lead_source ? <Badge variant="outline" className="text-xs">{formatStatus(c.lead_source)}</Badge> : '-'}
                      </TableCell>
                      <TableCell><QualityBadge record={c} type="client" /></TableCell>
                      <TableCell className="text-sm">{formatDate(c.created_at)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/clients/${c.client_id}`); }}>
                              <Eye className="h-4 w-4 mr-2" />View
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600" onClick={(e) => { e.stopPropagation(); setClientToDelete(c); setDeleteDialogOpen(true); }}>
                              <Trash2 className="h-4 w-4 mr-2" />Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Client Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>Add New Client</DialogTitle>
                <DialogDescription>Enter the client's details below or import from screenshots.</DialogDescription>
              </div>
              <Button variant="outline" size="sm" data-testid="clear-client-form-btn" className="text-xs h-8 shrink-0" onClick={() => setNewClient({ first_name: '', last_name: '', company_name: '', email: '', phone: '', dob: '', current_address: '', postcode: '', income: '', employment_type: '', credit_issues: false, credit_issues_notes: '', lead_source: '', referral_partner_name: '', fact_find_complete: false, vulnerable_customer: false, advice_type: '', additional_applicants: [] })}>
                <RotateCcw className="h-3 w-3 mr-1" /> Clear Form
              </Button>
            </div>
          </DialogHeader>
          <div className="pb-2">
            <ScreenshotImport type="client" onExtracted={(data) => {
              const applicants = data.applicants || [data];
              const primary = applicants[0] || {};
              setNewClient(prev => ({
                ...prev,
                first_name: primary.first_name || prev.first_name,
                last_name: primary.last_name || prev.last_name,
                company_name: primary.company_name || prev.company_name,
                email: primary.email || prev.email,
                phone: primary.phone || prev.phone,
                dob: primary.dob || prev.dob,
                current_address: primary.current_address || prev.current_address,
                postcode: primary.postcode || prev.postcode,
                employment_type: primary.employment_type || prev.employment_type,
                income: primary.income ? String(primary.income) : prev.income,
                additional_applicants: applicants.length > 1
                  ? applicants.slice(1).map(ap => ({
                      first_name: ap.first_name || '',
                      last_name: ap.last_name || '',
                      dob: ap.dob || '',
                      email: ap.email || '',
                      phone: ap.phone || '',
                      income: ap.income ? String(ap.income) : '',
                      employment_type: ap.employment_type || '',
                    }))
                  : prev.additional_applicants,
              }));
            }} />
          </div>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2 space-y-2"><Label>Company Name <span className="text-slate-400 text-xs font-normal">(optional — for limited company applications)</span></Label><Input value={newClient.company_name} onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })} placeholder="e.g. Smith Properties Ltd" data-testid="client-company-name" /></div>
            <div className="space-y-2"><Label>First Name *</Label><Input value={newClient.first_name} onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value })} data-testid="client-first-name" /></div>
            <div className="space-y-2"><Label>Last Name *</Label><Input value={newClient.last_name} onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value })} data-testid="client-last-name" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} data-testid="client-email" /></div>
            <div className="space-y-2"><Label>Phone</Label><Input value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} data-testid="client-phone" /></div>
            <div className="space-y-2"><Label>Date of Birth</Label><Input type="date" value={newClient.dob} onChange={(e) => setNewClient({ ...newClient, dob: e.target.value })} data-testid="client-dob" /></div>
            <div className="space-y-2"><Label>Postcode</Label><Input value={newClient.postcode} onChange={(e) => setNewClient({ ...newClient, postcode: e.target.value })} data-testid="client-postcode" /></div>
            <div className="col-span-2 space-y-2"><Label>Current Address</Label><Input value={newClient.current_address} onChange={(e) => setNewClient({ ...newClient, current_address: e.target.value })} data-testid="client-address" /></div>
            <div className="space-y-2"><Label>Income</Label><CurrencyInput value={newClient.income} onChange={(e) => setNewClient({ ...newClient, income: e.target.value })} data-testid="client-income" /></div>
            <div className="space-y-2">
              <Label>Employment Type</Label>
              <Select value={newClient.employment_type || 'none'} onValueChange={(v) => setNewClient({ ...newClient, employment_type: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="client-employment"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select...</SelectItem>
                  {EMPLOYMENT_TYPES.map((t) => <SelectItem key={t} value={t}>{formatStatus(t)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Lead Source</Label>
              <Select value={newClient.lead_source || 'none'} onValueChange={(v) => setNewClient({ ...newClient, lead_source: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="client-lead-source"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select...</SelectItem>
                  {LEAD_SOURCES.map((s) => <SelectItem key={s} value={s}>{formatStatus(s)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {newClient.lead_source === 'referral' && (
              <div className="space-y-2"><Label>Referral Partner</Label><Input value={newClient.referral_partner_name} onChange={(e) => setNewClient({ ...newClient, referral_partner_name: e.target.value })} /></div>
            )}
            <div className="col-span-2 flex items-center space-x-2">
              <Checkbox id="credit" checked={newClient.credit_issues} onCheckedChange={(c) => setNewClient({ ...newClient, credit_issues: c })} />
              <Label htmlFor="credit">Credit Issues</Label>
            </div>
            {newClient.credit_issues && (
              <div className="col-span-2 space-y-2"><Label>Credit Issue Notes</Label><Textarea value={newClient.credit_issues_notes} onChange={(e) => setNewClient({ ...newClient, credit_issues_notes: e.target.value })} data-testid="client-credit-notes" /></div>
            )}
            <div className="flex items-center space-x-2">
              <Checkbox id="vulnerable" checked={newClient.vulnerable_customer} onCheckedChange={(c) => setNewClient({ ...newClient, vulnerable_customer: c })} />
              <Label htmlFor="vulnerable">Vulnerable Customer</Label>
            </div>

            {/* Additional Applicants */}
            <div className="col-span-2 border-t border-slate-200 dark:border-slate-700 pt-4 mt-2">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold dark:text-slate-100">Additional Applicants</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setNewClient({ ...newClient, additional_applicants: [...newClient.additional_applicants, { first_name: '', last_name: '', dob: '', email: '', phone: '', income: '', employment_type: '' }] })}
                  data-testid="add-applicant-in-form-btn"
                >
                  <UserPlus className="h-4 w-4 mr-2" />Add Additional Applicant
                </Button>
              </div>
              {newClient.additional_applicants.map((ap, idx) => (
                <div key={idx} className="p-4 mb-3 bg-transparent border border-slate-300 dark:border-slate-600 rounded-lg" data-testid={`new-applicant-${idx}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Applicant {idx + 2}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-red-600 h-7"
                      onClick={() => {
                        const updated = [...newClient.additional_applicants];
                        updated.splice(idx, 1);
                        setNewClient({ ...newClient, additional_applicants: updated });
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />Remove
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs">First Name *</Label><Input value={ap.first_name} onChange={(e) => { const u = [...newClient.additional_applicants]; u[idx] = { ...u[idx], first_name: e.target.value }; setNewClient({ ...newClient, additional_applicants: u }); }} data-testid={`new-applicant-fname-${idx}`} /></div>
                    <div className="space-y-1"><Label className="text-xs">Surname *</Label><Input value={ap.last_name} onChange={(e) => { const u = [...newClient.additional_applicants]; u[idx] = { ...u[idx], last_name: e.target.value }; setNewClient({ ...newClient, additional_applicants: u }); }} data-testid={`new-applicant-lname-${idx}`} /></div>
                    <div className="space-y-1"><Label className="text-xs">Date of Birth</Label><Input type="date" max="9999-12-31" value={ap.dob} onChange={(e) => { const u = [...newClient.additional_applicants]; u[idx] = { ...u[idx], dob: e.target.value }; setNewClient({ ...newClient, additional_applicants: u }); }} /></div>
                    <div className="space-y-1"><Label className="text-xs">Email</Label><Input type="email" value={ap.email} onChange={(e) => { const u = [...newClient.additional_applicants]; u[idx] = { ...u[idx], email: e.target.value }; setNewClient({ ...newClient, additional_applicants: u }); }} /></div>
                    <div className="space-y-1"><Label className="text-xs">Phone</Label><Input value={ap.phone} onChange={(e) => { const u = [...newClient.additional_applicants]; u[idx] = { ...u[idx], phone: e.target.value }; setNewClient({ ...newClient, additional_applicants: u }); }} /></div>
                    <div className="space-y-1"><Label className="text-xs">Income</Label><CurrencyInput value={ap.income} onChange={(e) => { const u = [...newClient.additional_applicants]; u[idx] = { ...u[idx], income: e.target.value }; setNewClient({ ...newClient, additional_applicants: u }); }} /></div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Employment Type</Label>
                      <Select value={ap.employment_type || 'none'} onValueChange={(v) => { const u = [...newClient.additional_applicants]; u[idx] = { ...u[idx], employment_type: v === 'none' ? '' : v }; setNewClient({ ...newClient, additional_applicants: u }); }}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">Select...</SelectItem>{EMPLOYMENT_TYPES.map((t) => <SelectItem key={t} value={t}>{formatStatus(t)}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
              {newClient.additional_applicants.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-2">No additional applicants. Click the button above for joint applications.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleAddClient} disabled={!newClient.first_name || !newClient.last_name} data-testid="save-client-btn">Save Client</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Client</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {clientToDelete?.first_name} {clientToDelete?.last_name}? This will also delete all associated cases, documents, and tasks.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteClient} data-testid="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {selectedIds.size} Client{selectedIds.size > 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedIds.size} selected client{selectedIds.size > 1 ? 's' : ''}? This will also delete all associated cases, documents, and tasks. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)} disabled={bulkDeleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkDeleting} data-testid="confirm-bulk-delete-clients-btn">
              {bulkDeleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</> : `Delete ${selectedIds.size} Client${selectedIds.size > 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;
